import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../../ui/button";
import { Spinner } from "../../ui/spinner";
import {
  PROFILE_LOAD_ERROR_HINT,
  PROFILE_LOAD_ERROR_RETRIES_EXHAUSTED_HINT,
  PROFILE_LOAD_ERROR_SIGN_IN_AGAIN,
  PROFILE_LOAD_ERROR_SIGN_OUT,
  PROFILE_LOAD_ERROR_TITLE,
} from "../constants";

// Each Retry already makes up to 3 /users/me attempts with backoff, so a few
// manual retries is plenty — past that the problem isn't transient, and
// endless retrying just hammers the API.
export const PROFILE_LOAD_MAX_MANUAL_RETRIES = 3;

export type ProfileLoadErrorProps = {
  onRetry: () => void;
  onSignOut: () => void;
  // Disables both actions and shows a spinner on Retry while a reload is
  // in flight, so it can't be double-submitted.
  isRetrying: boolean;
  // "page": inside the app after a reload — the user is signed in, so the
  // exit is "Sign out". "sign-in": right after submitting the sign-in form,
  // where the user doesn't consider themselves signed in yet, so the exit is
  // "Sign in again", styled like the form's own submit button. The action
  // (end the half-created session) is the same either way.
  variant?: "page" | "sign-in";
  maxRetries?: number;
};

// Retry and Sign out share one style on the page variant.
const PAGE_ACTION_CLASSNAME =
  "rounded-md bg-[var(--deep-sky-denim)] px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:bg-[var(--deep-sky-denim)] hover:opacity-90 cursor-pointer";

const RETRY_SPINNER_LABEL = (
  <>
    <Spinner className="size-4" />
    Retrying...
  </>
);

export default function ProfileLoadError({
  onRetry,
  onSignOut,
  isRetrying,
  variant = "page",
  maxRetries = PROFILE_LOAD_MAX_MANUAL_RETRIES,
}: ProfileLoadErrorProps) {
  const [retriesUsed, setRetriesUsed] = useState(0);
  const retriesExhausted = retriesUsed >= maxRetries && !isRetrying;

  const handleRetry = (): void => {
    setRetriesUsed((count) => count + 1);
    onRetry();
  };

  const isSignInVariant = variant === "sign-in";

  return (
    <div
      role="alert"
      className={
        isSignInVariant
          ? "flex w-full flex-col items-center gap-3 text-center"
          : "flex h-full w-full flex-col items-center justify-center gap-3 px-6 py-12 text-center"
      }
    >
      <p className="text-[14px] font-medium text-[#181D27]">{PROFILE_LOAD_ERROR_TITLE}</p>
      <p className="text-[13px] text-[#717680]">
        {retriesExhausted ? PROFILE_LOAD_ERROR_RETRIES_EXHAUSTED_HINT : PROFILE_LOAD_ERROR_HINT}
      </p>

      {isSignInVariant ? (
        <div className="mt-2 flex w-full flex-col items-center gap-4">
          {retriesExhausted ? null : (
            <button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center gap-2 text-[13px] font-medium text-[#1876F2] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline cursor-pointer"
            >
              {isRetrying ? RETRY_SPINNER_LABEL : "Retry"}
            </button>
          )}
          <Button
            type="button"
            onClick={onSignOut}
            disabled={isRetrying}
            className="w-full h-12 cursor-pointer rounded-md bg-[#1876F2] text-white hover:bg-[#1876F2]/90 disabled:opacity-50"
          >
            {PROFILE_LOAD_ERROR_SIGN_IN_AGAIN}
            <ArrowRight className="size-4 text-white" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {retriesExhausted ? null : (
            <Button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying}
              className={PAGE_ACTION_CLASSNAME}
            >
              {isRetrying ? RETRY_SPINNER_LABEL : "Retry"}
            </Button>
          )}
          <Button
            type="button"
            onClick={onSignOut}
            disabled={isRetrying}
            className={PAGE_ACTION_CLASSNAME}
          >
            {PROFILE_LOAD_ERROR_SIGN_OUT}
          </Button>
        </div>
      )}
    </div>
  );
}
